"use memo"

import {
  AutopilotTaskDto,
  useInvalidateUseGetAutopilotTasksForCampaign,
  useToggleTaskSteps,
} from '@urbanx/agx.activities.client'
import {
  AgxLoadingState,
  AgxModal,
  AgxSpacer,
  AgxStepsIndicator,
  AgxTypography,
  useToast,
} from '@urbanx/agx.ui-components2'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import TaskModalActionButtons from './TaskModalActionButtons'
import { CampaignDetailModel, DocumentGroup } from '@urbanx/agx.campaigns.client'
import TasksFilesList from 'pages/campaigns/components/campaignTasks/TasksFilesList'
import { TaskModalPageProps } from './TaskModalPageProps'
import {
  emitTaskUpdate,
  useAutopilotTaskUpdates,
} from 'hooks/useAutopilotTaskUpdates'
import { applyTaskUpdateToTasks } from 'utils/tasks'
import { TaskUpdatedNotification } from 'types/pushNotifications'

export interface TaskModalContentConfig {
  documents: DocumentGroup[]
  isPending?: boolean
  onSaveCurrentPage?: (task: AutopilotTaskDto) => void | Promise<void>
  onSaveAllPages?: (tasks: AutopilotTaskDto[]) => void | Promise<void>
  renderPage: (props: TaskModalPageProps) => React.ReactNode
  completeDisabled?: boolean
  saveAndExitDisabled?: boolean
  initialPageIndex?: number
}

interface TaskModalDialogProps {
  title: string
  tasks: AutopilotTaskDto[]
  open: boolean
  onClose: () => void
  campaignId: string
  campaign: CampaignDetailModel
  contentConfig: TaskModalContentConfig
}

const TaskModalDialog = ({
  title,
  tasks,
  open,
  onClose,
  campaignId,
  campaign,
  contentConfig,
}: TaskModalDialogProps) => {
  const {
    documents,
    isPending = false,
    onSaveCurrentPage,
    onSaveAllPages,
    renderPage,
    completeDisabled = false,
    saveAndExitDisabled = false,
    initialPageIndex = 0,
  } = contentConfig
  const [currentPage, setCurrentPage] = useState(0)
  const [localTasks, setLocalTasks] = useState<AutopilotTaskDto[]>(tasks)
  const [isPersistingCurrentPage, setIsPersistingCurrentPage] = useState(false)
  const persistedTasksRef = useRef<AutopilotTaskDto[]>(tasks)
  const renderedPageRef = useRef<React.ReactNode>(null)
  const wasOpenRef = useRef(open)
  const queryClient = useQueryClient()
  const resolvedInitialPageIndex = Math.min(
    Math.max(initialPageIndex, 0),
    Math.max(tasks.length - 1, 0)
  )

  useEffect(() => {
    if (!open) {
      setLocalTasks(tasks)
      persistedTasksRef.current = tasks
      setCurrentPage(resolvedInitialPageIndex)
    }
  }, [tasks, open, resolvedInitialPageIndex])

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setLocalTasks(tasks)
      persistedTasksRef.current = tasks
      setCurrentPage(resolvedInitialPageIndex)
    }

    wasOpenRef.current = open
  }, [open, tasks, resolvedInitialPageIndex])

  const currentTask = localTasks[currentPage]
  const areAllStepsCompleted = localTasks.every(
    (task) =>
      (task.taskSteps?.length ?? 0) > 0 &&
      (task.taskSteps ?? []).every((step) => step.isCompleted)
  )

  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign()

  const { pushToast } = useToast()

  const applyTaskUpdate = (taskUpdate: TaskUpdatedNotification) => {
    persistedTasksRef.current = applyTaskUpdateToTasks(
      persistedTasksRef.current,
      taskUpdate
    )
    if (isPersistingCurrentPage) return

    setLocalTasks((prevTasks) => applyTaskUpdateToTasks(prevTasks, taskUpdate))
  }

  useAutopilotTaskUpdates(applyTaskUpdate)

  const { toggleTaskSteps } = useToggleTaskSteps({
    onSuccess: (updatedTask) => {
      if (!updatedTask) return

      queryClient.setQueryData<AutopilotTaskDto[]>(
        [
          'getAutopilotTasksForCampaign',
          campaignId,
          updatedTask.contractId ?? undefined,
        ],
        (previousTasks) =>
          previousTasks
            ? applyTaskUpdateToTasks(previousTasks, { task: updatedTask })
            : previousTasks
      )
      emitTaskUpdate({ task: updatedTask })
    },
    onError: () => {
      pushToast({
        type: 'error',
        message: 'Failed to toggle task steps',
      })
    },
  })

  const toggleTaskStep = (stepOrder: number, isChecked: boolean) => {
    setLocalTasks((prev) =>
      prev.map((task, index) => {
        if (index !== currentPage) {
          return task
        }

        return {
          ...task,
          taskSteps:
            task.taskSteps?.map((step) =>
              step.order === stepOrder
                ? { ...step, isCompleted: isChecked }
                : step
            ) ?? [],
        }
      })
    )
  }

  const getChangedStepIds = (
    task: AutopilotTaskDto | undefined,
    page: number
  ) => {
    const persistedTask = persistedTasksRef.current[page]

    return (
      task?.taskSteps
        ?.filter((step) => {
          const persistedStep = persistedTask?.taskSteps?.find(
            (candidate) => candidate.order === step.order
          )

          return (
            (persistedStep?.isCompleted ?? false) !==
            (step.isCompleted ?? false)
          )
        })
        .map((step) => step.order ?? 0) ?? []
    )
  }

  const persistTaskSteps = async (
    task: AutopilotTaskDto | undefined,
    page: number
  ) => {
    const changedStepIds = getChangedStepIds(task, page)

    if (changedStepIds.length === 0 || !task?.category) return

    await toggleTaskSteps({
      campaignId,
      category: task.category,
      contractId: task.contractId,
      stepIds: changedStepIds,
    })
    await invalidateTasks(campaignId, task.contractId)
  }

  const persistAllPages = async () => {
    if (onSaveAllPages) {
      await onSaveAllPages(localTasks)
    } else if (currentTask) {
      await onSaveCurrentPage?.(currentTask)
    }

    for (const [page, task] of localTasks.entries()) {
      await persistTaskSteps(task, page)
    }
  }

  const onPrevious = async () => {
    if (isPersistingCurrentPage) return

    setCurrentPage((prev) => Math.max(prev - 1, 0))
  }

  const onNext = async () => {
    if (isPersistingCurrentPage) return

    setCurrentPage((prev) => Math.min(prev + 1, localTasks.length - 1))
  }

  const onSaveAndExit = async () => {
    if (isPersistingCurrentPage) return

    setIsPersistingCurrentPage(true)
    try {
      await persistAllPages()
      onClose()
    } catch {
      pushToast({
        type: 'error',
        message: 'Failed to save task changes',
      })
    } finally {
      setIsPersistingCurrentPage(false)
    }
  }

  const onComplete = async () => {
    if (isPersistingCurrentPage) return

    setIsPersistingCurrentPage(true)
    try {
      await persistAllPages()
      onClose()
    } catch {
      pushToast({
        type: 'error',
        message: 'Failed to save task changes',
      })
    } finally {
      setIsPersistingCurrentPage(false)
    }
  }

  const shouldRenderPage =
    open && currentTask && (!isPending || isPersistingCurrentPage)
  const shouldRenderLoading = open && isPending && !isPersistingCurrentPage
  let pageContent: React.ReactNode = null

  if (isPersistingCurrentPage) {
    pageContent = renderedPageRef.current
  } else if (shouldRenderPage) {
    pageContent = renderPage({
      task: currentTask,
      taskNumber: currentPage + 1,
      campaign,
      toggleTaskStep,
    })
    renderedPageRef.current = pageContent
  } else if (!open) {
    renderedPageRef.current = null
  }

  return (
    <AgxModal
      type="fullScreen"
      variant={'withDivider'}
      actionButtonContainerStyle={'withDivider'}
      setIsDialogOpen={onClose}
      isOpen={open}
      secondaryTitle={
        <AgxSpacer direction="horizontal" contentAlignment="center" fill>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              width: '100%',
              gap: 'var(--gap-xl)',
            }}
          >
            <AgxTypography
              type="body_ui_strong"
              size="medium"
              id="titlemodal"
              color="var(--text-primary)"
            >
              {title}
            </AgxTypography>
            <AgxStepsIndicator
              totalLevels={localTasks.length}
              currentLevel={currentPage + 1}
            />
          </div>
        </AgxSpacer>
      }
      content={
        <AgxSpacer
          direction="horizontal"
          padding={{
            top: 'md',
            bottom: 'md',
            left: 'xl',
            right: 'xl',
          }}
          fill
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '3fr 2fr',
              width: '100%',
              gap: 'var(--gap-xl)',
            }}
          >
            <AgxSpacer fill>
              <TasksFilesList files={documents} id={campaignId} />
            </AgxSpacer>
            <AgxSpacer direction="vertical" fill>
              {pageContent}
              {shouldRenderLoading ? <AgxLoadingState /> : null}
            </AgxSpacer>
          </div>
        </AgxSpacer>
      }
      actionButtons={
        <TaskModalActionButtons
          currentPage={currentPage}
          totalPages={localTasks.length}
          onPrevious={onPrevious}
          onNext={onNext}
          onComplete={onComplete}
          onSaveAndExit={onSaveAndExit}
          completeDisabled={completeDisabled || !areAllStepsCompleted}
          saveAndExitDisabled={saveAndExitDisabled}
          isSaving={isPersistingCurrentPage}
        />
      }
    />
  )
}

export default TaskModalDialog
